import { useEffect, useMemo, useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from 'shared/ui/card'
import { Button } from 'shared/ui/button'
import { Badge } from 'shared/ui/badge'
import { Input } from 'shared/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'shared/ui/select'
import { Label } from 'shared/ui/label'
import { Checkbox } from 'shared/ui/checkbox'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from 'shared/ui/dialog'
import { Separator } from 'shared/ui/separator'
import { Plus, Filter, ShieldPlus, Trash2, Search, Loader2 } from 'lucide-react'
import {
  searchWorkflowTemplateFields,
  type WorkflowTemplateFieldLookupDto,
} from 'modules/admin/api/workflowConfigurationService'
import type { WorkflowCondition, WorkflowRuleset, WorkflowStage } from 'modules/admin/types/workflowTypes'

interface WorkflowRulesetSectionProps {
  rulesets: WorkflowRuleset[]
  stages: WorkflowStage[]
  templateVersionId: string
  onAddRuleset: () => void
  onUpdateRuleset: (rulesetId: string, changes: Partial<WorkflowRuleset>) => void
}

export function WorkflowRulesetSection({
  rulesets,
  stages,
  templateVersionId,
  onAddRuleset,
  onUpdateRuleset,
}: WorkflowRulesetSectionProps) {
  const stageOptions = useMemo(
    () => stages.map((stage) => ({ value: stage.stageId, label: `${stage.seqOrder}. ${stage.name}` })),
    [stages],
  )

  const [fieldLookupState, setFieldLookupState] = useState<{ rulesetId: string; conditionId: string } | null>(null)
  const [fieldSearch, setFieldSearch] = useState('')
  const [fieldItems, setFieldItems] = useState<WorkflowTemplateFieldLookupDto[]>([])
  const [fieldLoading, setFieldLoading] = useState(false)
  const [fieldError, setFieldError] = useState<string | null>(null)

  useEffect(() => {
    let active = true
    if (!fieldLookupState) {
      return
    }
    if (!templateVersionId) {
      setFieldItems([])
      setFieldError('ต้องเลือก template version ก่อน')
      return
    }
    setFieldLoading(true)
    setFieldError(null)
    const handler = setTimeout(() => {
      searchWorkflowTemplateFields(templateVersionId, fieldSearch)
        .then((response) => {
          if (!active) return
          setFieldItems(response.items)
        })
        .catch(() => {
          if (!active) return
          setFieldItems([])
          setFieldError('ไม่สามารถโหลดข้อมูล field ได้')
        })
        .finally(() => {
          if (!active) return
          setFieldLoading(false)
        })
    }, 250)

    return () => {
      active = false
      clearTimeout(handler)
    }
  }, [fieldLookupState, fieldSearch, templateVersionId])

  const handleConditionChange = (
    rulesetId: string,
    conditionId: string,
    updates: Partial<WorkflowCondition>,
  ) => {
    const target = rulesets.find((ruleset) => ruleset.workflowRulesetId === rulesetId)
    if (!target) {
      return
    }
    const nextConditions = target.conditions.map((condition: WorkflowCondition) =>
      condition.workflowRuleConditionId === conditionId ? { ...condition, ...updates } : condition,
    )
    onUpdateRuleset(rulesetId, { conditions: nextConditions })
  }

  const handleRemoveCondition = (rulesetId: string, conditionId: string) => {
    const target = rulesets.find((ruleset) => ruleset.workflowRulesetId === rulesetId)
    if (!target) {
      return
    }
    const nextConditions = target.conditions.filter(
      (condition: WorkflowCondition) => condition.workflowRuleConditionId !== conditionId,
    )
    onUpdateRuleset(rulesetId, { conditions: nextConditions })
  }

  const closeFieldLookup = () => {
    setFieldLookupState(null)
    setFieldSearch('')
    setFieldItems([])
    setFieldError(null)
    setFieldLoading(false)
  }

  const handleSelectTemplateField = (option: WorkflowTemplateFieldLookupDto) => {
    if (!fieldLookupState) {
      return
    }
    handleConditionChange(fieldLookupState.rulesetId, fieldLookupState.conditionId, {
      fieldId: option.fieldId,
      fieldCode: option.fieldCode,
    })
    closeFieldLookup()
  }

  const handleToggleExcludeStage = (ruleset: WorkflowRuleset, stageId: string, checked: boolean) => {
    const current = new Set(ruleset.excludeStageIds ?? [])
    if (checked) {
      current.add(stageId)
    } else {
      current.delete(stageId)
    }
    onUpdateRuleset(ruleset.workflowRulesetId, { excludeStageIds: Array.from(current) })
  }

  return (
    <Card className="border border-gray-200">
      <CardHeader className="flex flex-col space-y-1">
        <CardTitle className="flex items-center justify-between text-base font-semibold text-gray-900">
          <span className="flex items-center gap-2">
            <Filter className="h-4 w-4 text-primary" />
            Ruleset & Routing
          </span>
          <Button size="sm" onClick={onAddRuleset}>
            <Plus className="mr-1 h-4 w-4" />
            Add Ruleset
          </Button>
        </CardTitle>
        <p className="text-sm text-gray-500">กำหนดเงื่อนไขเพื่อเลือก workflow stage ที่เหมาะสม (CAP stage)</p>
      </CardHeader>
      <CardContent className="space-y-4">
        {rulesets.map((ruleset) => (
          <div key={ruleset.workflowRulesetId} className="rounded-lg border border-gray-200 p-4 shadow-sm">
            <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
              <div>
                <Input
                  value={ruleset.name}
                  onChange={(event) => onUpdateRuleset(ruleset.workflowRulesetId, { name: event.target.value })}
                  placeholder="Ruleset name"
                />
                <Input
                  className="mt-2"
                  value={ruleset.description ?? ''}
                  onChange={(event) => onUpdateRuleset(ruleset.workflowRulesetId, { description: event.target.value })}
                  placeholder="Description"
                />
              </div>
              <div className="w-full md:w-60">
                <p className="text-xs font-medium text-gray-600">CAP Stage</p>
                <Select
                  value={ruleset.capStageId ?? ''}
                  onValueChange={(value) => onUpdateRuleset(ruleset.workflowRulesetId, { capStageId: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select stage" />
                  </SelectTrigger>
                  <SelectContent>
                    {stageOptions.map((stage) => (
                      <SelectItem key={stage.value} value={stage.value}>
                        {stage.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="mt-4 rounded-md border border-dashed border-gray-200 p-3">
              <p className="text-xs font-semibold text-gray-600">Exclude Stages</p>
              <p className="mt-1 text-[11px] text-gray-500">
                เลือก Approver Stage ที่ต้องตัดออกจาก flow เมื่อ ruleset นี้ match
              </p>
              {stageOptions.length > 0 ? (
                <div className="mt-3 grid grid-cols-1 gap-2 md:grid-cols-2">
                  {stageOptions.map((stage) => {
                    const checked = (ruleset.excludeStageIds ?? []).includes(stage.value)
                    return (
                      <label
                        key={stage.value}
                        className="flex items-start gap-2 rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm"
                      >
                        <Checkbox
                          checked={checked}
                          onCheckedChange={(value) =>
                            handleToggleExcludeStage(ruleset, stage.value, value === true)
                          }
                          className="mt-0.5"
                        />
                        <span className="text-gray-700">{stage.label}</span>
                      </label>
                    )
                  })}
                </div>
              ) : (
                <p className="mt-2 text-[11px] text-gray-500">ยังไม่มี stage ให้เลือก</p>
              )}
            </div>
            <div className="mt-4 space-y-2 rounded-md border border-dashed border-gray-200 p-3">
              <p className="text-xs font-semibold text-gray-600">Conditions</p>
              {ruleset.conditions.map((condition: WorkflowCondition) => (
                <div key={condition.workflowRuleConditionId} className="space-y-2 rounded-md bg-gray-50 p-3">
                  <div className="grid grid-cols-1 gap-2 md:grid-cols-3">
                    <div className="md:col-span-2">
                      <Label className="text-[10px] uppercase text-gray-500">Template Field</Label>
                      <div className="flex flex-wrap items-center gap-2">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          disabled={!templateVersionId}
                          onClick={() =>
                            setFieldLookupState({
                              rulesetId: ruleset.workflowRulesetId,
                              conditionId: condition.workflowRuleConditionId,
                            })
                          }
                        >
                          <Search className="mr-2 h-3.5 w-3.5" />
                          {condition.fieldCode && condition.fieldId
                            ? `${condition.fieldCode}`
                            : 'เลือก Field จาก Template'}
                        </Button>
                        {condition.fieldId ? <Badge variant="secondary">ID: {condition.fieldId}</Badge> : null}
                      </div>
                      {!templateVersionId ? (
                        <p className="text-[11px] text-red-500">ต้องเลือก template version ก่อน</p>
                      ) : null}
                    </div>
                    <div>
                      <Label className="text-[10px] uppercase text-gray-500">Operator</Label>
                      <Input
                        value={condition.operator}
                        onChange={(event) =>
                          handleConditionChange(ruleset.workflowRulesetId, condition.workflowRuleConditionId, {
                            operator: event.target.value,
                          })
                        }
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="text-[10px] uppercase text-gray-500">Value</Label>
                    <Input
                      value={condition.value ?? ''}
                      onChange={(event) =>
                        handleConditionChange(ruleset.workflowRulesetId, condition.workflowRuleConditionId, {
                          value: event.target.value,
                        })
                      }
                    />
                  </div>
                  <div className="flex justify-end">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="text-red-500"
                      onClick={() => handleRemoveCondition(ruleset.workflowRulesetId, condition.workflowRuleConditionId)}
                    >
                      <Trash2 className="mr-1 h-3.5 w-3.5" />
                      Remove
                    </Button>
                  </div>
                </div>
              ))}
              <Button
                variant="ghost"
                size="sm"
                className="text-xs text-primary"
                onClick={() => {
                  const newCondition: WorkflowCondition = {
                    workflowRuleConditionId: crypto.randomUUID?.() ?? Math.random().toString(36).slice(2),
                    workflowRulesetId: ruleset.workflowRulesetId,
                    fieldId: '',
                    fieldCode: '',
                    operator: '=',
                    value: '',
                  }
                  onUpdateRuleset(ruleset.workflowRulesetId, { conditions: [...ruleset.conditions, newCondition] })
                }}
              >
                <ShieldPlus className="mr-1 h-3.5 w-3.5" />
                เพิ่มเงื่อนไข
              </Button>
            </div>
          </div>
        ))}
        {rulesets.length === 0 ? <p className="text-sm text-gray-500">ยังไม่มี ruleset</p> : null}
      </CardContent>
      <Dialog open={Boolean(fieldLookupState)} onOpenChange={(open) => (!open ? closeFieldLookup() : null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>เลือก Template Field</DialogTitle>
          </DialogHeader>
          {templateVersionId ? (
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Search className="h-4 w-4 text-gray-400" />
                <Input
                  value={fieldSearch}
                  onChange={(event) => setFieldSearch(event.target.value)}
                  placeholder="ค้นหาด้วย field code หรือ label"
                />
              </div>
              <Separator />
              <div className="max-h-80 space-y-2 overflow-y-auto">
                {fieldLoading ? (
                  <div className="flex items-center justify-center py-8 text-sm text-gray-500">
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    กำลังโหลดข้อมูล...
                  </div>
                ) : fieldError ? (
                  <p className="py-4 text-sm text-red-500">{fieldError}</p>
                ) : fieldItems.length === 0 ? (
                  <p className="py-4 text-sm text-gray-500">ไม่พบ field ที่ตรงเงื่อนไข</p>
                ) : (
                  fieldItems.map((item) => (
                    <button
                      key={item.fieldId}
                      type="button"
                      onClick={() => handleSelectTemplateField(item)}
                      className="w-full rounded-md border border-gray-200 bg-white p-3 text-left hover:border-primary"
                    >
                      <p className="text-sm font-semibold text-gray-900">
                        {item.fieldCode} · {item.fieldLabel}
                      </p>
                      <p className="text-xs text-gray-500">
                        Section: {item.sectionCode} ({item.sectionTitle})
                      </p>
                    </button>
                  ))
                )}
              </div>
            </div>
          ) : (
            <p className="py-6 text-sm text-red-500">ต้องเลือก template version ก่อนจึงจะค้นหา field ได้</p>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={closeFieldLookup}>
              ปิด
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  )
}
